import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Badge,
  Button,
  DashboardCard,
  DashboardGrid,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  EmptyState,
  PageHeader,
  Skeleton,
  StatusBadge,
  cn,
} from '@zafroi/ui-kit';
import {
  ArrowRight,
  ChevronDown,
  Globe,
  Inbox,
  Mail,
  MessageCircle,
  MessageSquare,
  Phone,
  Plug,
  Plus,
  Radio,
  Settings,
  type LucideIcon,
} from 'lucide-react';
import { useChannels } from '@/hooks/useEngageQueries';
import { useTenant } from '@/services/tenant-context';
import { channelStatusVariants } from '@/types/status-variants';
import { relativeTime } from '@/lib/format';
import type { Channel, ChannelKind } from '@/types/engage';
import { EmailChannelSheet } from './EmailChannelSheet';
import { WhatsAppChannelSheet } from './WhatsAppChannelSheet';

/**
 * Map a raw `?error=<reason>` value from the OAuth callback to a friendly,
 * tenant-facing message. The runtime forwards a small set of well-known
 * reasons (`access_denied`, `missing_params`, …) plus raw exception messages
 * for anything unexpected — we squash both into something usable.
 */
function describeOAuthError(reason: string): string {
  if (reason === 'access_denied') {
    return 'Gmail authorisation was cancelled.';
  }
  if (reason === 'missing_params') {
    return 'Something went wrong — please try again.';
  }
  if (reason.startsWith('Google did not return a refresh token')) {
    return 'Already authorised before — please revoke access at myaccount.google.com/permissions and retry.';
  }
  if (reason.toLowerCase().includes('not configured')) {
    return 'OAuth is not yet configured by your operator (see CHANNELS.md).';
  }
  // Fall back to the raw reason, truncated so the toast stays readable.
  return reason.length > 160 ? `${reason.slice(0, 157)}…` : reason;
}

/**
 * ChannelsPage — Phase 1 of the Channels epic.
 *
 * Lists the tenant's channels (Email, WhatsApp, Portal, SMS, Webchat) with
 * status, identifier and last-synced. EMAIL is the only kind with a working
 * configure flow this phase — it covers identity + auto-respond template.
 * Other kinds get read-only / "coming soon" affordances per CHANNELS.md §3-7.
 */

interface KindMeta {
  icon: LucideIcon;
  /** Human label for the kind, used in section headers + add menu. */
  label: string;
  /** One-line value-prop shown on the card. */
  description: string;
  /** Phase-1 disposition for the row's primary CTA. */
  disposition:
    | 'configurable' // Email — fully editable today
    | 'preparing' // WhatsApp — Phase 2 placeholder
    | 'live' // Portal / Webchat — already running, info-only
    | 'later'; // SMS — not yet on the roadmap
}

const KIND_META: Record<ChannelKind, KindMeta> = {
  email: {
    icon: Mail,
    label: 'Email',
    description:
      'Receive customer emails, auto-acknowledge them, and turn each into a ticket.',
    disposition: 'configurable',
  },
  whatsapp: {
    icon: MessageSquare,
    label: 'WhatsApp',
    description:
      'Two-way WhatsApp Business engagement for pre-sales and support.',
    disposition: 'preparing',
  },
  portal: {
    icon: Globe,
    label: 'Customer portal',
    description:
      'Customer self-service portal — conversations, requests, and order tracking.',
    disposition: 'live',
  },
  webchat: {
    icon: MessageCircle,
    label: 'Site widget',
    description:
      'Embeddable site widget for live chat and lead capture.',
    disposition: 'live',
  },
  sms: {
    icon: Phone,
    label: 'SMS',
    description:
      'Transactional notifications and one-way SMS broadcasts. Coming later.',
    disposition: 'later',
  },
};

// Render channels in a stable order — most actionable first.
const KIND_ORDER: ChannelKind[] = ['email', 'whatsapp', 'portal', 'webchat', 'sms'];

function sortChannels(channels: Channel[]): Channel[] {
  return [...channels].sort((a, b) => {
    const ai = KIND_ORDER.indexOf(a.kind);
    const bi = KIND_ORDER.indexOf(b.kind);
    if (ai !== bi) return ai - bi;
    return a.label.localeCompare(b.label);
  });
}

export function ChannelsPage() {
  const query = useChannels();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentTenant } = useTenant();

  const [emailSheetOpen, setEmailSheetOpen] = useState(false);
  const [emailEditTarget, setEmailEditTarget] = useState<Channel | null>(null);
  const [whatsappSheetOpen, setWhatsappSheetOpen] = useState(false);
  // After a successful OAuth callback we capture the channel id so we can
  // auto-open its sheet once the channels list refetches and we can resolve
  // the id to a full Channel record.
  const pendingAutoOpenRef = useRef<string | null>(null);

  const channels = useMemo(() => sortChannels(query.data ?? []), [query.data]);

  const openEmailEditor = (channel: Channel | null) => {
    setEmailEditTarget(channel);
    setEmailSheetOpen(true);
  };

  // ── OAuth callback handler ──────────────────────────────────────────────
  // Runtime redirects here after the Google round-trip with either
  // `?connected=<channelId>` (success) or `?error=<reason>&channel=<id?>`
  // (failure). Both legs surface a toast and clear the query params so a
  // refresh doesn't re-trigger the handler.
  useEffect(() => {
    const connected = searchParams.get('connected');
    const error = searchParams.get('error');
    if (!connected && !error) return;

    if (connected) {
      // Refetch the channels list so the newly-connected status appears.
      queryClient.invalidateQueries({ queryKey: ['channels', currentTenant.id] });
      toast.success('Connected your Gmail.', {
        description:
          'Try the "Test connection" button to fetch your latest emails.',
      });
      // Remember the channel id; we'll auto-open the sheet as soon as the
      // refetched data lands (next effect, below).
      pendingAutoOpenRef.current = connected;
    } else if (error) {
      toast.error('Gmail connection failed', {
        description: describeOAuthError(error),
      });
    }

    // Clear the params so a tab refresh doesn't replay the toast / refetch.
    navigate('/channels', { replace: true });
    // Intentionally exclude `currentTenant.id` and `queryClient` from the dep
    // list — this should only fire once per URL change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Once the post-OAuth refetch finishes, pop the just-connected channel's
  // sheet open so the tenant sees the freshly-connected state and the
  // "Test connection" button right where they expect it.
  useEffect(() => {
    const targetId = pendingAutoOpenRef.current;
    if (!targetId || channels.length === 0) return;
    const target = channels.find((c) => c.id === targetId);
    if (target) {
      openEmailEditor(target);
      pendingAutoOpenRef.current = null;
    }
  }, [channels]);

  // ── Loading skeleton — five cards, matching the page layout ────────────
  if (query.isLoading) {
    return (
      <div className="space-y-6">
        <PageHeader

          title="Channels"
          description="Where your customers reach out. Email is live; WhatsApp and more, coming."
        />
        <DashboardGrid columns={3}>
          {[0, 1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-48 w-full" />
          ))}
        </DashboardGrid>
      </div>
    );
  }

  // ── Error state — friendly retry pointer ───────────────────────────────
  if (query.isError) {
    return (
      <div className="space-y-6">
        <PageHeader

          title="Channels"
          description="Where your customers reach out. Email is live; WhatsApp and more, coming."
        />
        <EmptyState
          icon={Plug}
          title="We couldn't load your channels"
          description="Try refreshing — if it persists, the runtime might be unreachable."
        />
      </div>
    );
  }

  // ── Empty state — friendly first-channel prompt ────────────────────────
  if (channels.length === 0) {
    return (
      <div className="space-y-6">
        <PageHeader

          title="Channels"
          description="Where your customers reach out. Email is live; WhatsApp and more, coming."
          actions={<AddChannelMenu onAddEmail={() => openEmailEditor(null)} />}
        />
        <EmptyState
          icon={Inbox}
          title="Add your first channel"
          description="Start with email — connect a support inbox and we'll auto-acknowledge every incoming message with a ticket reference."
          action={
            <Button size="sm" onClick={() => openEmailEditor(null)}>
              <Mail className="h-3.5 w-3.5" />
              Set up email
            </Button>
          }
        />
        <EmailChannelSheet
          open={emailSheetOpen}
          onOpenChange={setEmailSheetOpen}
          channel={emailEditTarget}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader

        title="Channels"
        description="Where your customers reach out. Email is live; WhatsApp and more, coming."
        actions={<AddChannelMenu onAddEmail={() => openEmailEditor(null)} />}
      />

      <DashboardGrid columns={3}>
        {channels.map((channel) => (
          <ChannelCard
            key={channel.id}
            channel={channel}
            onConfigureEmail={() => openEmailEditor(channel)}
            onConfigureWhatsApp={() => setWhatsappSheetOpen(true)}
          />
        ))}
      </DashboardGrid>

      <EmailChannelSheet
        open={emailSheetOpen}
        onOpenChange={setEmailSheetOpen}
        channel={emailEditTarget}
      />
      <WhatsAppChannelSheet
        open={whatsappSheetOpen}
        onOpenChange={setWhatsappSheetOpen}
      />
    </div>
  );
}

// ─── Per-channel card ──────────────────────────────────────────────────────

interface ChannelCardProps {
  channel: Channel;
  onConfigureEmail: () => void;
  onConfigureWhatsApp: () => void;
}

function ChannelCard({
  channel,
  onConfigureEmail,
  onConfigureWhatsApp,
}: ChannelCardProps) {
  const meta = KIND_META[channel.kind] ?? {
    icon: Radio,
    label: channel.kind,
    description: '',
    disposition: 'later' as const,
  };

  const cta = renderCta({
    disposition: meta.disposition,
    onConfigureEmail,
    onConfigureWhatsApp,
  });

  return (
    <DashboardCard
      title={channel.label}
      description={meta.description}
      icon={meta.icon}
      headerActions={
        <StatusBadge
          status={channel.status}
          variants={channelStatusVariants}
        />
      }
      footer={
        <div className="flex items-center justify-between gap-3">
          <span className="text-[11px] text-muted-foreground">
            {channel.lastSyncedAt
              ? `Synced ${relativeTime(channel.lastSyncedAt)}`
              : 'Never synced'}
          </span>
          {cta}
        </div>
      }
      className={cn(
        meta.disposition === 'later' && 'opacity-75',
      )}
    >
      <dl className="space-y-2 text-xs">
        <div className="flex items-center justify-between gap-3">
          <dt className="text-muted-foreground">Identifier</dt>
          <dd className="truncate font-mono text-foreground">
            {channel.identifier ?? (
              <span className="italic text-muted-foreground">Not set</span>
            )}
          </dd>
        </div>
        <div className="flex items-center justify-between gap-3">
          <dt className="text-muted-foreground">Kind</dt>
          <dd>
            <Badge tone="neutral" withDot={false}>
              {meta.label}
            </Badge>
          </dd>
        </div>
        {channel.kind === 'email' && (
          <div className="flex items-center justify-between gap-3">
            <dt className="text-muted-foreground">Auto-respond</dt>
            <dd className="text-foreground">
              {channel.templateSubject || channel.templateBody ? (
                <Badge tone="success" withDot={false}>
                  Template ready
                </Badge>
              ) : (
                <Badge tone="warning" withDot={false}>
                  Needs template
                </Badge>
              )}
            </dd>
          </div>
        )}
      </dl>
    </DashboardCard>
  );
}

interface CtaProps {
  disposition: KindMeta['disposition'];
  onConfigureEmail: () => void;
  onConfigureWhatsApp: () => void;
}

function renderCta({
  disposition,
  onConfigureEmail,
  onConfigureWhatsApp,
}: CtaProps) {
  switch (disposition) {
    case 'configurable':
      return (
        <Button variant="outline" size="sm" onClick={onConfigureEmail}>
          <Settings className="h-3.5 w-3.5" />
          Configure
        </Button>
      );
    case 'preparing':
      return (
        <Button variant="ghost" size="sm" onClick={onConfigureWhatsApp}>
          What to prepare
          <ArrowRight className="h-3.5 w-3.5" />
        </Button>
      );
    case 'live':
      return (
        <Badge tone="success" withDot>
          Live today
        </Badge>
      );
    case 'later':
    default:
      return (
        <Badge tone="neutral" withDot={false}>
          Coming later
        </Badge>
      );
  }
}

// ─── + Add channel menu ────────────────────────────────────────────────────

interface AddChannelMenuProps {
  onAddEmail: () => void;
}

function AddChannelMenu({ onAddEmail }: AddChannelMenuProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm">
          <Plus className="h-3.5 w-3.5" />
          Add channel
          <ChevronDown className="h-3.5 w-3.5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>Available now</DropdownMenuLabel>
        <DropdownMenuItem onSelect={onAddEmail}>
          <Mail className="h-3.5 w-3.5" />
          Email
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuLabel>Coming next</DropdownMenuLabel>
        <DropdownMenuItem disabled>
          <MessageSquare className="h-3.5 w-3.5" />
          WhatsApp Business
          <span className="ml-auto text-[10px] uppercase tracking-wider text-muted-foreground">
            Soon
          </span>
        </DropdownMenuItem>
        <DropdownMenuItem disabled>
          <Phone className="h-3.5 w-3.5" />
          SMS
          <span className="ml-auto text-[10px] uppercase tracking-wider text-muted-foreground">
            Later
          </span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
